import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import SimpleRichTextEditor from './SimpleRichTextEditor';

const GET_PACKAGES = gql`
  query GetPackages {
    packages {
      id
      name
      price
      currency
      discount_percentage
      details
      notes
      is_active
      max_staff_members
      max_whatsapp_instances
      max_contacts
      max_contact_groups
      max_templates
      max_bulk_messages_per_day
      duration_days
      package_type
      priority_support
      features
      country
      terms_and_conditions
      created_at
      updated_at
    }
  }
`;

const CREATE_PACKAGE = gql`
  mutation CreatePackage($input: CreatePackageInput!) {
    createPackage(input: $input) {
      id
      name
      price
      currency
      is_active
      max_staff_members
      max_contacts
      package_type
    }
  }
`;

const UPDATE_PACKAGE = gql`
  mutation UpdatePackage($id: ID!, $input: UpdatePackageInput!) {
    updatePackage(id: $id, input: $input) {
      id
      name
      price
      currency
      is_active
      max_staff_members
      max_contacts
      package_type
    }
  }
`;

const DELETE_PACKAGE = gql`
  mutation DeletePackage($id: ID!) {
    deletePackage(id: $id)
  }
`;

const TOGGLE_PACKAGE_STATUS = gql`
  mutation TogglePackageStatus($id: ID!) {
    togglePackageStatus(id: $id) {
      id
      is_active
    }
  }
`;

const PackageManagement = () => {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState('all');
    const [selectedPackageType, setSelectedPackageType] = useState('all');

    // Country options
    const countries = [
        { code: 'IN', name: 'India' },
        { code: 'US', name: 'United States' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'CA', name: 'Canada' },
        { code: 'AU', name: 'Australia' },
        { code: 'DE', name: 'Germany' },
        { code: 'FR', name: 'France' },
        { code: 'JP', name: 'Japan' },
        { code: 'SG', name: 'Singapore' },
        { code: 'AE', name: 'UAE' }
    ];

    const { data, loading, error, refetch } = useQuery(GET_PACKAGES, {
        fetchPolicy: 'cache-and-network'
    });

    // Handle data updates with useEffect instead of onCompleted
    useEffect(() => {
        if (data) {
            console.log('📦 GraphQL packages data received:', data);
        }
    }, [data]);

    const [createPackage] = useMutation(CREATE_PACKAGE);
    const [updatePackage] = useMutation(UPDATE_PACKAGE);
    const [deletePackage] = useMutation(DELETE_PACKAGE);
    const [togglePackageStatus] = useMutation(TOGGLE_PACKAGE_STATUS);

    const [formData, setFormData] = useState({
        name: '',
        price: 0,
        currency: 'INR',
        discount_percentage: 0,
        details: '',
        notes: '',
        is_active: true,
        // New privilege fields
        max_staff_members: 1,
        max_whatsapp_instances: 1,
        max_contacts: 1000,
        max_contact_groups: 10,
        max_templates: 10,
        max_bulk_messages_per_day: 100,
        duration_days: 30,
        package_type: 'basic',
        priority_support: false,
        country: 'IN',
        features: {
            api_access: false,
            analytics: 'basic',
            export_data: false,
            custom_branding: false
        },
        terms_and_conditions: ''
    });

    const resetForm = () => {
        setFormData({
            name: '',
            price: 0,
            currency: 'INR',
            discount_percentage: 0,
            details: '',
            notes: '',
            is_active: true,
            // New privilege fields
            max_staff_members: 1,
            max_whatsapp_instances: 1,
            max_contacts: 1000,
            max_contact_groups: 10,
            max_templates: 10,
            max_bulk_messages_per_day: 100,
            duration_days: 30,
            package_type: 'basic',
            priority_support: false,
            country: 'IN',
            features: {
                api_access: false,
                analytics: 'basic',
                export_data: false,
                custom_branding: false
            },
            terms_and_conditions: ''
        });
        setShowCreateForm(false);
        setEditingPackage(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const input = {
                ...formData,
                price: parseFloat(formData.price),
                discount_percentage: parseFloat(formData.discount_percentage)
            };

            if (editingPackage) {
                await updatePackage({
                    variables: {
                        id: editingPackage.id,
                        input
                    }
                });
            } else {
                await createPackage({
                    variables: { input }
                });
            }
            resetForm();
            refetch();
        } catch (err) {
            console.error('Error saving package:', err);
            alert('Error saving package: ' + err.message);
        }
    };

    const handleEdit = (pkg) => {
        console.log('📝 Editing package:', pkg); // Debug log
        setFormData({
            name: pkg.name || '',
            price: pkg.price || 0,
            currency: pkg.currency || 'INR',
            discount_percentage: pkg.discount_percentage || 0,
            details: pkg.details || '',
            notes: pkg.notes || '',
            is_active: pkg.is_active ?? true,
            // Privilege fields - use actual values from database
            max_staff_members: pkg.max_staff_members || 1,
            max_whatsapp_instances: pkg.max_whatsapp_instances || 1,
            max_contacts: pkg.max_contacts || 1000,
            max_contact_groups: pkg.max_contact_groups || 10,
            max_templates: pkg.max_templates || 10,
            max_bulk_messages_per_day: pkg.max_bulk_messages_per_day || 100,
            duration_days: pkg.duration_days || 30,
            package_type: pkg.package_type || 'basic',
            priority_support: pkg.priority_support || false,
            country: pkg.country || 'IN',
            features: pkg.features || {
                api_access: false,
                analytics: 'basic',
                export_data: false,
                custom_branding: false
            },
            terms_and_conditions: pkg.terms_and_conditions || ''
        });
        setEditingPackage(pkg);
        setShowCreateForm(true);
    };

    const handleDelete = async (id) => {
        setShowDeleteModal(id);
    };

    const confirmDelete = async () => {
        try {
            await deletePackage({ variables: { id: showDeleteModal } });
            refetch();
            setShowDeleteModal(null);
        } catch (err) {
            console.error('Error deleting package:', err);
            alert('Error deleting package: ' + err.message);
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            await togglePackageStatus({ variables: { id } });
            refetch();
        } catch (err) {
            console.error('Error toggling package status:', err);
            alert('Error toggling status: ' + err.message);
        }
    };

    const filteredPackages = data?.packages?.filter(pkg => {
        const matchesSearch = !searchTerm ||
            pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (pkg.details && pkg.details.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesCountry = selectedCountry === 'all' || pkg.country === selectedCountry;

        const matchesPackageType = selectedPackageType === 'all' || pkg.package_type === selectedPackageType;

        return matchesSearch && matchesCountry && matchesPackageType;
    }) || [];

    if (loading && !data) return <div className="p-8 text-center text-gray-400 animate-pulse uppercase tracking-[0.3em] text-[10px]">Loading Packages...</div>;
    if (error) return <div className="p-8 text-center text-red-500 uppercase tracking-wider text-[10px]">Error: {error.message}</div>;

    return (
        <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div>
                        <h2 className="font-medium">Package Management</h2>
                        <p className="text-[10px] text-gray-400 font-normal uppercase tracking-wider mt-0.5">Manage Subscription Plans</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                        {/* Search Field */}
                        <div className="relative flex-1 lg:flex-none lg:w-64">
                            <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-bulky-blue-500 transition-colors z-10">search</span>
                            <input
                                type="text"
                                placeholder="Search Plans..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="form-input pl-11 h-[42px]"
                            />
                        </div>

                        {/* Country Filter */}
                        <div className="relative flex-1 lg:flex-none">
                            <select
                                value={selectedCountry}
                                onChange={(e) => setSelectedCountry(e.target.value)}
                                className="form-select pl-4 pr-10 h-[42px]"
                            >
                                <option value="all">All Countries</option>
                                {countries.map(country => (
                                    <option key={country.code} value={country.code}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                            <span className="material-icons-round absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none">expand_more</span>
                        </div>

                        {/* Tier Filter */}
                        <div className="relative flex-1 lg:flex-none">
                            <select
                                value={selectedPackageType}
                                onChange={(e) => setSelectedPackageType(e.target.value)}
                                className="form-select pl-4 pr-10 h-[42px]"
                            >
                                <option value="all">All Types</option>
                                <option value="basic">Basic</option>
                                <option value="pro">Pro</option>
                                <option value="enterprise">Enterprise</option>
                                <option value="custom">Custom</option>
                            </select>
                            <span className="material-icons-round absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none">expand_more</span>
                        </div>

                        <button
                            className="btn-primary py-2 px-4 whitespace-nowrap"
                            onClick={() => setShowCreateForm(true)}
                        >
                            <span className="material-icons-round text-sm">add</span>
                            <span>Create Package</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                {filteredPackages.map(pkg => (
                    <div key={pkg.id} className={`bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden group hover:shadow-2xl hover:shadow-bulky-blue-500/20 hover:-translate-y-1 transition-all duration-500 relative min-h-[420px] flex flex-col ${!pkg.is_active ? 'opacity-60' : ''}`}>
                        {/* Status bar with glow on hover */}
                        <div className={`h-1.5 w-full transition-all duration-500 group-hover:h-2 ${pkg.is_active ? 'bg-bulky-blue-500 shadow-[0_0_20px_rgba(40,92,237,0.4)]' : 'bg-gray-200'}`}></div>

                        <div className="p-6 relative flex-1 flex flex-col">
                            {/* Decorative background glow on hover */}
                            <div className="absolute top-0 right-0 w-24 h-24 bg-bulky-blue-500/0 group-hover:bg-bulky-blue-500/5 rounded-full blur-3xl transition-all duration-700 pointer-events-none"></div>

                            <div className="flex justify-between items-start mb-4">
                                <div className="flex-1 min-w-0">
                                    <h2 className="font-medium">{pkg.name}</h2>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[8px] font-normal text-bulky-navy-500 uppercase tracking-wider px-2 py-0.5 bg-gray-50 rounded-md border border-gray-100 whitespace-nowrap">ID: {pkg.id?.substring(0, 6)}</span>
                                        <span className={`text-[8px] font-normal uppercase tracking-wider px-2 py-0.5 rounded-md border whitespace-nowrap ${pkg.is_active ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                                            {pkg.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        <span className="text-[8px] font-normal text-blue-600 uppercase tracking-wider px-2 py-0.5 bg-blue-50 rounded-md border border-blue-100 whitespace-nowrap">
                                            {pkg.country || 'IN'}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right ml-3 flex-shrink-0">
                                    <div className="text-xl font-display font-normal text-bulky-navy-900 tracking-tighter leading-none mb-1 group-hover:scale-105 transition-transform origin-right duration-500">
                                        {pkg.currency === 'INR' ? '₹' : '$'}{pkg.price}
                                    </div>
                                    {pkg.discount_percentage > 0 && (
                                        <div className="text-[8px] font-normal text-emerald-500 uppercase tracking-wider animate-pulse whitespace-nowrap">
                                            -{pkg.discount_percentage}% OFF
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Privilege Display */}
                            <div className="space-y-3 mb-4 flex-1">
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-100/50 p-4 rounded-2xl border border-blue-100 shadow-sm">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className="material-icons-round text-blue-500 text-sm">engineering</span>
                                            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest opacity-80">Staff</span>
                                        </div>
                                        <div className="text-xl font-bold text-blue-800">{pkg.max_staff_members || 1}</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-emerald-50 to-green-100/50 p-4 rounded-2xl border border-emerald-100 shadow-sm">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className="material-icons-round text-emerald-500 text-sm">contacts</span>
                                            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest opacity-80">Contacts</span>
                                        </div>
                                        <div className="text-xl font-bold text-emerald-800">{(pkg.max_contacts || 1000).toLocaleString()}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-1.5">
                                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded-lg border border-blue-100 text-center">
                                        <span className="material-icons-round text-blue-500 text-[10px] block mb-0.5">chat</span>
                                        <div className="text-[10px] font-normal text-blue-700">{pkg.max_whatsapp_instances || 1}</div>
                                        <div className="text-[7px] text-blue-600 uppercase tracking-wider">WA</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-2 rounded-lg border border-orange-100 text-center">
                                        <span className="material-icons-round text-orange-500 text-[10px] block mb-0.5">template_add</span>
                                        <div className="text-[10px] font-normal text-orange-700">{pkg.max_templates || 10}</div>
                                        <div className="text-[7px] text-orange-600 uppercase tracking-wider">Temp</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-bulky-blue-50 to-blue-50 p-2 rounded-lg border border-bulky-blue-100 text-center">
                                        <span className="material-icons-round text-bulky-blue-500 text-[10px] block mb-0.5">send</span>
                                        <div className="text-[10px] font-normal text-bulky-blue-700">{pkg.max_bulk_messages_per_day || 100}</div>
                                        <div className="text-[7px] text-bulky-blue-600 uppercase tracking-wider">Daily</div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between p-2.5 bg-gray-50/50 rounded-xl border border-gray-100">
                                    <div className="flex items-center gap-1.5">
                                        <span className="material-icons-round text-gray-500 text-xs">schedule</span>
                                        <span className="text-[8px] font-normal text-gray-600 uppercase tracking-wider">Duration</span>
                                    </div>
                                    <span className="text-xs font-normal text-gray-700">{pkg.duration_days || 30} days</span>
                                </div>

                                {pkg.priority_support && (
                                    <div className="flex items-center gap-1.5 p-2 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-200">
                                        <span className="material-icons-round text-yellow-600 text-xs">support_agent</span>
                                        <span className="text-[8px] font-normal text-yellow-700 uppercase tracking-wider">Priority Support</span>
                                    </div>
                                )}
                            </div>

                            {pkg.details && (
                                <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-100 group-hover:border-bulky-blue-100 group-hover:bg-white transition-all duration-500 mb-4">
                                    <p className="text-[10px] font-bold text-bulky-navy-400 uppercase tracking-[0.2em] mb-2 opacity-70">Plan Details</p>
                                    <p className="text-[13px] font-medium text-bulky-navy-700 leading-relaxed line-clamp-2">
                                        {pkg.details}
                                    </p>
                                </div>
                            )}

                            {/* Action Area - always visible, positioned at bottom */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                                <div className="flex gap-2">
                                    <div className="relative group/btn">
                                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-bulky-navy-800 text-white text-[7px] font-normal px-2 py-1 rounded-md opacity-0 scale-50 group-hover/btn:opacity-100 group-hover/btn:scale-100 transition-all duration-300 pointer-events-none whitespace-nowrap shadow-xl z-50 uppercase tracking-wider border border-white/10">
                                            View Details
                                            <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-bulky-navy-800 rotate-45 border-r border-b border-white/10"></div>
                                        </div>
                                        <button
                                            onClick={() => setShowDetailsModal(pkg)}
                                            className="w-10 h-10 rounded-xl bg-white shadow-lg shadow-bulky-navy-900/5 border border-gray-200 text-bulky-navy-700 hover:border-bulky-blue-500 hover:text-bulky-blue-500 hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center"
                                        >
                                            <span className="material-icons-round text-lg">visibility</span>
                                        </button>
                                    </div>

                                    <div className="relative group/btn">
                                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-bulky-navy-800 text-white text-[7px] font-normal px-2 py-1 rounded-md opacity-0 scale-50 group-hover/btn:opacity-100 group-hover/btn:scale-100 transition-all duration-300 pointer-events-none whitespace-nowrap shadow-xl z-50 uppercase tracking-wider border border-white/10">
                                            Edit
                                            <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-bulky-navy-800 rotate-45 border-r border-b border-white/10"></div>
                                        </div>
                                        <button
                                            onClick={() => handleEdit(pkg)}
                                            className="w-10 h-10 rounded-xl bg-white shadow-md shadow-bulky-navy-900/5 border border-gray-100 text-bulky-navy-400 hover:border-bulky-blue-500 hover:text-bulky-blue-500 hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center"
                                        >
                                            <span className="material-icons-round text-lg">edit</span>
                                        </button>
                                    </div>

                                    <div className="relative group/btn">
                                        <div className={`absolute -top-8 left-1/2 -translate-x-1/2 text-white text-[7px] font-normal px-2 py-1 rounded-md opacity-0 scale-50 group-hover/btn:opacity-100 group-hover/btn:scale-100 transition-all duration-300 pointer-events-none whitespace-nowrap shadow-xl z-50 uppercase tracking-wider border border-white/10 ${pkg.is_active ? 'bg-yellow-600' : 'bg-emerald-600'}`}>
                                            {pkg.is_active ? 'Disable' : 'Enable'}
                                            <div className={`absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rotate-45 border-r border-b border-white/10 ${pkg.is_active ? 'bg-yellow-600' : 'bg-emerald-600'}`}></div>
                                        </div>
                                        <button
                                            onClick={() => handleToggleStatus(pkg.id)}
                                            className={`w-10 h-10 rounded-xl shadow-md shadow-bulky-navy-900/5 border transition-all duration-300 flex items-center justify-center hover:scale-110 active:scale-95 ${pkg.is_active ? 'bg-yellow-50 text-yellow-600 border-yellow-100 hover:bg-yellow-500 hover:text-white' : 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-500 hover:text-white'}`}
                                        >
                                            <span className="material-icons-round text-lg">{pkg.is_active ? 'power_off' : 'power'}</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="relative group/btn">
                                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[7px] font-normal px-2 py-1 rounded-md opacity-0 scale-50 group-hover/btn:opacity-100 group-hover/btn:scale-100 transition-all duration-300 pointer-events-none whitespace-nowrap shadow-xl z-50 uppercase tracking-wider border border-white/10">
                                        Delete
                                        <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-red-600 rotate-45 border-r border-b border-white/10"></div>
                                    </div>
                                    <button
                                        onClick={() => handleDelete(pkg.id)}
                                        className="w-10 h-10 rounded-xl bg-red-50 shadow-md shadow-red-900/5 border border-red-100 text-red-500 hover:bg-red-500 hover:text-white hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center"
                                    >
                                        <span className="material-icons-round text-lg">delete_sweep</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {filteredPackages.length === 0 && (
                    <div className="col-span-full py-32 bg-white rounded-[3rem] border-2 border-dashed border-gray-100 flex flex-col items-center justify-center text-center">
                        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6 text-gray-200">
                            <span className="material-icons-round text-5xl">inventory</span>
                        </div>
                        <h2 className="font-medium">No Plans Found</h2>
                        <p className="text-[11px] font-normal text-bulky-navy-500 uppercase tracking-[0.3em]">No subscription plans match your search.</p>
                    </div>
                )}
            </div>

            {showCreateForm && createPortal(
                <div className="modal-overlay">
                    <div className="modal-container">
                        {/* Header */}
                        <div className="modal-header bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-950">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white border border-white/10 shadow-inner">
                                    <span className="material-icons-round text-2xl">inventory_2</span>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white tracking-tight leading-none">{editingPackage ? 'Update Package' : 'Create New Package'}</h2>
                                    <p className="text-bulky-navy-300 text-[11px] font-semibold tracking-wider mt-1.5 opacity-80 uppercase">Set up plan details and limits</p>
                                </div>
                            </div>
                            <button
                                onClick={resetForm}
                                className="w-11 h-11 hover:bg-white/10 text-white rounded-2xl flex items-center justify-center transition-all bg-white/5 border border-white/5 shadow-lg"
                            >
                                <span className="material-icons-round text-2xl">close</span>
                            </button>
                        </div>

                        {/* Form Content */}
                        <form onSubmit={handleSubmit} className="modal-content">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {/* Basic Info Section */}
                                <div className="form-section lg:col-span-3">
                                    <h3 className="form-section-title">
                                        <span className="material-icons-round text-blue-500 text-sm">inventory_2</span>
                                        Basic Information
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="form-label">Package Name</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                className="form-input"
                                                placeholder="e.g. Premium Plan"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">Country</label>
                                            <select
                                                value={formData.country}
                                                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                                className="form-select"
                                            >
                                                {countries.map(country => (
                                                    <option key={country.code} value={country.code}>
                                                        {country.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="form-label">Plan Type</label>
                                            <select
                                                value={formData.package_type}
                                                onChange={(e) => setFormData({ ...formData, package_type: e.target.value })}
                                                className="form-select"
                                            >
                                                <option value="basic">Basic</option>
                                                <option value="pro">Pro</option>
                                                <option value="enterprise">Enterprise</option>
                                                <option value="custom">Custom</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Economic Section */}
                                <div className="form-section md:col-span-1 lg:col-span-1">
                                    <h3 className="form-section-title">
                                        <span className="material-icons-round text-emerald-500 text-sm">payments</span>
                                        Pricing
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex gap-2">
                                            <div className="w-20">
                                                <label className="form-label">Currency</label>
                                                <select
                                                    value={formData.currency}
                                                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                                    className="form-select"
                                                >
                                                    <option value="INR">₹</option>
                                                    <option value="USD">$</option>
                                                </select>
                                            </div>
                                            <div className="flex-1">
                                                <label className="form-label">Base Price</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    required
                                                    value={formData.price}
                                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="form-label">Discount (%)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.discount_percentage}
                                                onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                                                className="form-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Technical Specs Section */}
                                <div className="form-section md:col-span-1 lg:col-span-2">
                                    <h3 className="form-section-title">
                                        <span className="material-icons-round text-orange-500 text-sm">description</span>
                                        Description
                                    </h3>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="form-label">Plan Details</label>
                                            <textarea
                                                rows="1"
                                                value={formData.details}
                                                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                                                className="form-input min-h-[38px] resize-none"
                                                placeholder="Describe the plan features..."
                                            ></textarea>
                                        </div>
                                        <div>
                                            <label className="form-label">Internal Notes</label>
                                            <input
                                                type="text"
                                                value={formData.notes}
                                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                                className="form-input"
                                                placeholder="Admin notes (not visible to users)..."
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Terms and Conditions Section */}
                                <div className="form-section lg:col-span-3">
                                    <h3 className="form-section-title">
                                        <span className="material-icons-round text-red-500 text-sm">gavel</span>
                                        Terms and Conditions
                                    </h3>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="form-label">Legal Terms & Conditions</label>
                                            <SimpleRichTextEditor
                                                value={formData.terms_and_conditions}
                                                onChange={(value) => setFormData({ ...formData, terms_and_conditions: value })}
                                                placeholder="Enter terms and conditions for this package..."
                                                className="w-full"
                                            />
                                            <p className="text-xs text-gray-500 mt-2">
                                                Define legal terms, conditions, and policies that apply to this package. This will be displayed to users before purchase.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Privilege Matrix Section */}
                                <div className="form-section lg:col-span-3">
                                    <h3 className="form-section-title">
                                        <span className="material-icons-round text-bulky-blue-500 text-sm">settings</span>
                                        Plan Limits
                                    </h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                                        <div>
                                            <label className="form-label">Staff</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.max_staff_members}
                                                onChange={(e) => setFormData({ ...formData, max_staff_members: parseInt(e.target.value) || 1 })}
                                                className="form-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">Contacts</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.max_contacts}
                                                onChange={(e) => setFormData({ ...formData, max_contacts: parseInt(e.target.value) || 1000 })}
                                                className="form-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">WhatsApp</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.max_whatsapp_instances}
                                                onChange={(e) => setFormData({ ...formData, max_whatsapp_instances: parseInt(e.target.value) || 1 })}
                                                className="form-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">Groups</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.max_contact_groups}
                                                onChange={(e) => setFormData({ ...formData, max_contact_groups: parseInt(e.target.value) || 10 })}
                                                className="form-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">Templates</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.max_templates}
                                                onChange={(e) => setFormData({ ...formData, max_templates: parseInt(e.target.value) || 10 })}
                                                className="form-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">Daily</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.max_bulk_messages_per_day}
                                                onChange={(e) => setFormData({ ...formData, max_bulk_messages_per_day: parseInt(e.target.value) || 100 })}
                                                className="form-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">Days</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.duration_days}
                                                onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 30 })}
                                                className="form-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-3 flex items-center gap-6 border-t border-gray-100 pt-3">
                                        <div className="toggle-switch-card" onClick={() => setFormData({ ...formData, priority_support: !formData.priority_support })}>
                                            <button
                                                type="button"
                                                className={`toggle-switch ${formData.priority_support ? 'bg-bulky-emerald-500' : 'bg-gray-200'}`}
                                            >
                                                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition duration-200 ease-in-out ${formData.priority_support ? 'translate-x-4' : 'translate-x-0.5'} mt-0.5`} />
                                            </button>
                                            <span className="toggle-switch-label">Priority Support</span>
                                        </div>

                                        <div className="toggle-switch-card" onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}>
                                            <button
                                                type="button"
                                                className={`toggle-switch ${formData.is_active ? 'bg-bulky-emerald-500' : 'bg-gray-200'}`}
                                            >
                                                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition duration-200 ease-in-out ${formData.is_active ? 'translate-x-4' : 'translate-x-0.5'} mt-0.5`} />
                                            </button>
                                            <span className="toggle-switch-label">Active Status</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="modal-footer">
                            <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                            <button type="button" onClick={handleSubmit} className="btn-primary">Save Package</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && createPortal(
                <div className="modal-overlay">
                    <div className="modal-container max-w-sm">
                        <div className="modal-header bg-red-600 from-red-600 to-red-700">
                            <div className="flex items-center gap-2">
                                <span className="material-icons-round text-white text-sm">warning</span>
                                <h2 className="font-medium">Delete Package</h2>
                            </div>
                            <button onClick={() => setShowDeleteModal(null)} className="text-white hover:bg-white/20 p-1 rounded">
                                <span className="material-icons-round text-sm">close</span>
                            </button>
                        </div>
                        <div className="modal-content text-center py-6">
                            <p className="text-sm text-gray-600">Are you sure you want to delete this package? This action cannot be undone.</p>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-2">Permanent Action</p>
                        </div>
                        <div className="modal-footer">
                            <button onClick={() => setShowDeleteModal(null)} className="btn-secondary">Cancel</button>
                            <button onClick={confirmDelete} className="btn-danger">Delete Package</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Package Details Modal */}
            {showDetailsModal && createPortal(
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-950">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white border border-white/10 shadow-inner">
                                    <span className="material-icons-round text-2xl">visibility</span>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white tracking-tight leading-none">{showDetailsModal.name}</h2>
                                    <p className="text-bulky-navy-300 text-[11px] font-semibold tracking-wider mt-1.5 opacity-80 uppercase">Package Details</p>
                                </div>
                            </div>
                            <button onClick={() => setShowDetailsModal(null)} className="w-11 h-11 hover:bg-white/10 text-white rounded-2xl flex items-center justify-center transition-all bg-white/5 border border-white/5 shadow-lg">
                                <span className="material-icons-round text-2xl">close</span>
                            </button>
                        </div>

                        <div className="modal-content">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100/50">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="material-icons-round text-blue-500 text-sm">group</span>
                                        <span className="text-[10px] font-normal text-blue-600 uppercase tracking-wider">Staff Limit</span>
                                    </div>
                                    <div className="text-2xl font-normal text-bulky-navy-800">{showDetailsModal.max_staff_members || 1}</div>
                                </div>
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100/50">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="material-icons-round text-emerald-500 text-sm">contacts</span>
                                        <span className="text-[10px] font-normal text-emerald-600 uppercase tracking-wider">Contacts</span>
                                    </div>
                                    <div className="text-2xl font-normal text-bulky-navy-800">{(showDetailsModal.max_contacts || 1000).toLocaleString()}</div>
                                </div>
                                <div className="bg-orange-50/50 p-4 rounded-xl border border-orange-100/50">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="material-icons-round text-orange-500 text-sm">template_add</span>
                                        <span className="text-[10px] font-normal text-orange-600 uppercase tracking-wider">Templates</span>
                                    </div>
                                    <div className="text-2xl font-normal text-bulky-navy-800">{showDetailsModal.max_templates || 10}</div>
                                </div>
                                <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100/50">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="material-icons-round text-indigo-500 text-sm">schedule</span>
                                        <span className="text-[10px] font-normal text-indigo-600 uppercase tracking-wider">Duration</span>
                                    </div>
                                    <div className="text-2xl font-normal text-bulky-navy-800">{showDetailsModal.duration_days || 30} <span className="text-xs font-normal">Days</span></div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div className="form-section">
                                    <h2 className="form-section-title">Plan Details</h2>
                                    <p className="text-xs font-normal text-gray-600 leading-relaxed">{showDetailsModal.details || 'No details provided.'}</p>
                                </div>
                                <div className="form-section">
                                    <h2 className="form-section-title">Internal Notes</h2>
                                    <p className="text-xs font-normal text-gray-600 italic leading-relaxed">{showDetailsModal.notes || 'No notes available.'}</p>
                                </div>
                            </div>

                            {/* Terms and Conditions */}
                            {showDetailsModal.terms_and_conditions && (
                                <div className="form-section mt-4">
                                    <h2 className="form-section-title">
                                        <span className="material-icons-round text-red-500 text-sm">gavel</span>
                                        Terms and Conditions
                                    </h2>
                                    <div className="bg-red-50 rounded-xl p-4 border border-red-100 max-h-60 overflow-y-auto">
                                        <div 
                                            className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: showDetailsModal.terms_and_conditions }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 mt-4">
                                <div className="flex items-center gap-2">
                                    <span className="material-icons-round text-gray-400 text-sm">public</span>
                                    <span className="text-[10px] font-normal text-gray-500 uppercase tracking-wider">Country</span>
                                </div>
                                <span className="text-sm font-normal text-bulky-navy-800 uppercase leading-none">{showDetailsModal.country || 'Global'}</span>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button onClick={() => setShowDetailsModal(null)} className="btn-secondary">Close</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default PackageManagement;
